import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useHistory } from "react-router-dom";
import ArrowForwardRoundedIcon from "@mui/icons-material/ArrowForwardRounded";
import ArrowBackRoundedIcon from "@mui/icons-material/ArrowBackRounded";
import Collapse from "@mui/material/Collapse";

import Box from "components/common/Box";
import Stack from "components/common/Stack";
import Text from "components/common/Text";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import AllInOneFooter from "components/allInOneDashboard/AllInOneFooter";
import SentimentPricingSection from "components/Product-Info-Pages/Sentiment/SentimentPricingSection";
import { UNIFIED_DASHBOARD_PATH } from "components/allInOneDashboard/constants/productRouteMap";
import { config } from "config/index";
import { thirdpartyApis } from "stores/thirdparty/thirdpartyApis";
import { thirdpartyParsers } from "stores/thirdparty/thirdpartyParsers";
import { sentimentTrackerOnboardingActions } from "stores/SentimentTracker/sentimentTrackerOnboarding/sentimentTrackerOnboardingAction";
import { localStore } from "stores/localStorage";

const ONBOARDING_PATH = "/sentiment-tracker/onboarding/overview";

const ACCENT = "#4E63A4";
const HERO_GRADIENT = "linear-gradient(162deg, #151A2A 1.5%, #4E5E8E 98%)";
const HERO_GRADIENT_MOBILE =
  "linear-gradient(136deg, #000618 19%, #435489 98%)";
const CTA_GRADIENT = "linear-gradient(270deg, #5167AA 0%, #171D30 100%)";
const CTA_GRADIENT_MOBILE =
  "linear-gradient(-76deg, #556CB3 0.6%, #141929 99%)";
const YEAR_CARD_BG = "#6F82BF";
const FEATURE_BORDER = "#E5E7EB";
const FOR_YOU_BORDER = "#D5DEFB";
const FACT_BORDER = "#E2E8F0";
const FACT_DOT = "#5E74B5";

const img = (name) =>
  `/images/sentiment-product-info/${encodeURIComponent(name)}`;

const ICONS = {
  horse: img("horse.png"),
  check: img("icon.svg"),
  frame: "/images/prime-product-info/Frame.svg", // white sparkles on year card
  frameAbout: img("Frame.svg"), // blue sparkles for mobile About (white card)
  tech1: img("technology-_1_ 1.svg"),
  dashboard: img("dashboard 1.svg"),
  clock: img("clock 2.svg"),
  tech2: img("technology-_2_ 1.svg"),
  tracking: img("tracking 1.svg"),
  momentum: img("momentum 1.svg"),
  consistency: img("consistency 1.svg"),
  noStopping: img("no-stopping 1.svg"),
  shield: img("hero-shield.svg"),
  target: img("hero-target.svg"),
  chartSpline: img("hero-chart.svg"),
};

const HERO_BENEFITS = [
  { icon: ICONS.shield, label: "Decodes institutional positions" },
  { icon: ICONS.target, label: "Real time derivatives data" },
  { icon: ICONS.chartSpline, label: "Anticipate moves not everyone sees" },
];

const HERO_SUBHEAD =
  "Ai-powered insights designed to help you identify short-term opportunities and timely profit-booking signals.";
const HERO_SUBHEAD_MOBILE =
  "AI-powered insights designed to help you identify short-term opportunities and timely profit-booking signals.";

const FEATURES = [
  {
    icon: ICONS.tech1,
    text: "AI Powered trade ideas designed for short term market opportunities.",
  },
  {
    icon: ICONS.dashboard,
    text: "High conviction entry & exit signals to support timely trading decisions",
  },
  {
    icon: ICONS.clock,
    text: "Real time market sentiment to understand directional market bias",
  },
  {
    icon: ICONS.tech2,
    text: "Advanced options analytics including Open Interest, Max Pain and PCR",
  },
  {
    icon: ICONS.tracking,
    text: "Institutional activity tracking with FII - DII buying and selling insights",
  },
  {
    icon: ICONS.momentum,
    text: "Momentum and trend alerts to help identify changing market conditions",
  },
  {
    icon: ICONS.consistency,
    text: "Built for disciplined tracking with timely profit booking opportunities",
  },
  {
    icon: ICONS.noStopping,
    text: "No Minimum investment requirement, making it accessible for traders of different capital sizes",
    fullWidth: true,
  },
];

const FOR_YOU_CARDS = [
  { label: "Trade", description: "Trade with a Short Term Investment Approach" },
  { label: "Ideas", description: "Want AI backed trade ideas" },
  { label: "Opportunity", description: "Prefer timely profit booking opportunities" },
  { label: "Track", description: "Track market sentimentbefore takig trades" },
  {
    label: "Activity",
    description: "Want to monitor institutional buying and selling activity",
  },
  {
    label: "Trading Strategy",
    description: "Use index or stock options in your trading strategy",
  },
];

const QUICK_FACTS = [
  { label: "Focus", value: "Market Sentiment" },
  { label: "Signals", value: "Entry & Exit" },
  { label: "Minimum Capital", value: "-" },
  { label: "Investor", value: "Active Trader" },
  { label: "Coverage", value: "Index & Stock Options" },
  { label: "holding", value: "Intraday" },
];

/** Mobile Figma copy (7069:9340 / 7069:8775 / 7111:6688) */
const MOBILE_ABOUT_ITEMS = [
  {
    icon: ICONS.frameAbout,
    text: (
      <>
        <Box component="span" sx={{ fontWeight: 600 }}>
          AI engine live
        </Box>{" "}
        in Indian Market since{" "}
        <Box component="span" sx={{ fontWeight: 600 }}>
          2016
        </Box>
      </>
    ),
  },
  {
    icon: ICONS.tech1,
    text: "AI-powered trade ideas designed for short-term market opportunities.",
  },
  {
    icon: ICONS.dashboard,
    text: "High-conviction entry & exit signals to support timely trading decisions.",
  },
  {
    icon: ICONS.clock,
    text: "Real-time market sentiment to understand directional market bias.",
  },
  {
    icon: ICONS.tech2,
    text: "Advanced options analytics including Open Interest, Max Pain and PCR.",
  },
  {
    icon: ICONS.tracking,
    text: "Institutional activity tracking with FII-DII buying and selling insights.",
  },
  {
    icon: ICONS.momentum,
    text: "Momentum and trend alerts to help identify changing market conditions.",
  },
  {
    icon: ICONS.consistency,
    text: "Built for disciplined trading with timely profit-booking opportunities.",
  },
  {
    icon: ICONS.noStopping,
    text: "No minimum investment requirement, making it accessible for traders of different capital sizes.",
  },
];

const MOBILE_FOR_YOU = [
  "Trade with a short-term investment approach.",
  "Want AI-backed trade ideas.",
  "Prefer timely profit-booking opportunities.",
  "Track market sentiment before taking trades.",
  "Want to monitor institutional buying and selling activity.",
  "Use Index or Stock Options in your trading strategy.",
];

const MOBILE_QUICK_FACTS = [
  { label: "Focus", value: "Market Sentiment" },
  { label: "Signals", value: "Entry & Exit" },
  { label: "Minimum Capital", value: "-" },
  { label: "Investor", value: "Active Traders" },
  { label: "Coverage", value: "Index & Stock Option" },
  { label: "Holding", value: "Intraday" },
];

const MOBILE_ABOUT_PREVIEW = 3;

const pagePad = {
  px: { xs: 2.5, md: 7.5 },
  maxWidth: 1320,
  mx: "auto",
  width: "100%",
};

const FeatureCard = ({ icon, text }) => (
  <Stack
    spacing={1.5}
    sx={{
      p: 3,
      height: "100%",
      borderRadius: "16px",
      bgcolor: "#fff",
      border: `1px solid ${FEATURE_BORDER}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
    }}
  >
    <Box component="img" src={icon} alt="" sx={{ width: 32, height: 32 }} />
    <Text sx={{ fontSize: 15, fontWeight: 500, color: "#111827", lineHeight: 1.45 }}>
      {text}
    </Text>
  </Stack>
);

const ChecklistCard = ({ label, description }) => (
  <Stack
    spacing={1.5}
    sx={{
      p: 3,
      height: "100%",
      borderRadius: "12px",
      bgcolor: "#fff",
      border: `1px solid ${FOR_YOU_BORDER}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.05)",
      position: "relative",
      overflow: "hidden",
    }}
  >
    <Box
      sx={{
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        height: 3,
        bgcolor: ACCENT,
      }}
    />
    <Stack direction="row" spacing={1.5} alignItems="center">
      
      <Box
        component="img"
        src={ICONS.check}
        alt=""
        sx={{ width: 36, height: 36, flexShrink: 0 }}
      />
      <Text sx={{ fontSize: 15, fontWeight: 600, color: "#374151", lineHeight: "20px" }}>
        {label}
      </Text>
    </Stack>
    <Text sx={{ fontSize: 13, color: "#313131", lineHeight: "20px" }}>{description}</Text>
  </Stack>
);

const FactCard = ({ label, value }) => (
  <Stack
    spacing={1.25}
    sx={{
      p: 3,
      borderRadius: "16px",
      bgcolor: "#fff",
      border: `1px solid ${FACT_BORDER}`,
      boxShadow: "0 4px 12px rgba(0,0,0,0.04)",
    }}
  >
    <Stack direction="row" spacing={1} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
      <Text sx={{ fontSize: 14, color: "#6B7280" }}>{label}</Text>
    </Stack>
    <Text sx={{ fontSize: 20, fontWeight: 700, color: "#111827" }}>{value}</Text>
  </Stack>
);

const MobileAboutRow = ({ icon, text }) => (
  <Stack direction="row" spacing={1.5} alignItems="flex-start">
    <Box
      component="img"
      src={icon}
      alt=""
      sx={{ width: 20, height: 20, flexShrink: 0, mt: "2px" }}
    />
    <Text sx={{ fontSize: 14, color: "#475569", lineHeight: "21px" }}>{text}</Text>
  </Stack>
);

const MobileForYouRow = ({ text }) => (
  <Stack
    direction="row"
    spacing={2}
    alignItems="center"
    sx={{
      p: 2,
      minHeight: 63,
      borderRadius: "16px",
      bgcolor: "rgba(255,255,255,0.12)",
      border: "1px solid rgba(255,255,255,0.12)",
      boxShadow: "0 10px 12px rgba(0,0,0,0.1)",
      backdropFilter: "blur(7px)",
    }}
  >
    
    <Box
      sx={{
        width: 24,
        height: 24,
        flexShrink: 0,
        display: "grid",
        placeItems: "center",
      }}
    >
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: "#5E74B5" }} />
    </Box>
    <Text sx={{ fontSize: 14, fontWeight: 500, color: "#fff", lineHeight: 1.35 }}>
      {text}
    </Text>
  </Stack>
);

const MobileFactTile = ({ label, value }) => (
  <Stack
    spacing={1.25}
    sx={{
      flex: 1,
      minWidth: 0,
      p: 2,
      borderRadius: "16px",
      bgcolor: "#fff",
      border: `1px solid ${FACT_BORDER}`,
      boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
    }}
  >
    <Stack direction="row" spacing={1.25} alignItems="center">
      <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: FACT_DOT }} />
      <Text sx={{ fontSize: 12, color: "#000", lineHeight: "16px" }}>{label}</Text>
    </Stack>
    <Text sx={{ fontSize: 16, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
      {value}
    </Text>
  </Stack>
);

const SentimentProductInfo = () => {
  const history = useHistory();
  const dispatch = useDispatch();
  const [plans, setPlans] = useState([]);
  const [aboutExpanded, setAboutExpanded] = useState(false);

  useEffect(() => {
    dispatch(sentimentTrackerOnboardingActions.getOnboardingData());
    thirdpartyApis
      .getSubscriptionPlans({ productCode: config.productCodes.SENTIMENT })
      .then((res) => setPlans(thirdpartyParsers.subscriptionPlans(res) || []))
      .catch(() => setPlans([]));
  }, [dispatch]);

  const handleStartInvesting = () => {
    localStore.setActiveProduct(config.productCodes.SENTIMENT);
    history.push(ONBOARDING_PATH);
  };

  const handleBack = () => {
    history.push(UNIFIED_DASHBOARD_PATH);
  };

  const middleFeatures = FEATURES.slice(1, -1);
  const fullWidthFeature = FEATURES[FEATURES.length - 1];
  const aboutPreview = MOBILE_ABOUT_ITEMS.slice(0, MOBILE_ABOUT_PREVIEW);
  const aboutRest = MOBILE_ABOUT_ITEMS.slice(MOBILE_ABOUT_PREVIEW);

  return (
    <Box sx={{ bgcolor: "#fff" }}>
      {/* Hero — desktop Figma 8707:12476 / mobile 7069:8761 */}
      <Box
        sx={{
          position: "relative",
          overflow: "hidden",
          background: { xs: HERO_GRADIENT_MOBILE, md: HERO_GRADIENT },
          px: { xs: 3, md: 7.5 },
          pt: { xs: 2.5, md: 4 },
          pb: { xs: 0, md: 0 },
          minHeight: { md: 640 },
          borderRadius: { xs: "0 0 24px 24px", md: 0 },
          boxShadow: {
            xs: "0 12px 28px -10px rgba(99,23,176,0.1)",
            md: "none",
          },
        }}
      >
        <Box sx={{ maxWidth: 1320, mx: "auto", position: "relative", zIndex: 1 }}>
          <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ mb: { xs: 2.5, md: 5 } }}
          >
            <IconButton
              onClick={handleBack}
              aria-label="Back to Explore"
              sx={{
                width: { xs: 36, md: 42 },
                height: { xs: 36, md: 42 },
                borderRadius: "12px",
                bgcolor: "rgba(255,255,255,0.08)",
                border: "1px solid #C3C3C3",
                boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)",
                p: 0,
                "&:hover": { bgcolor: "rgba(255,255,255,0.2)" },
              }}
            >
              <ArrowBackRoundedIcon sx={{ fontSize: { xs: 20, md: 24 }, color: "#fff" }} />
            </IconButton>
            <Stack spacing={{ xs: 0.5, md: "9px" }}>
              <Text
                sx={{
                  fontSize: { xs: 18, md: 32 },
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: { xs: "22px", md: "26px" },
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Jarvis Sentiment
              </Text>
              <Text
                sx={{
                  fontSize: 14,
                  fontWeight: 400,
                  color: "rgba(255,255,255,0.7)",
                  lineHeight: "20px",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Investing powered by AI
              </Text>
            </Stack>
          </Stack>

          {/* Desktop hero */}
          <Stack
            direction="row"
            alignItems="flex-start"
            justifyContent="space-between"
            spacing={4}
            sx={{ display: { xs: "none", md: "flex" }, pb: 5, minHeight: 520 }}
          >
            <Stack spacing={0} sx={{ flex: 1, maxWidth: 624, pt: 1 }}>
              <Text
                sx={{
                  fontSize: 64,
                  fontWeight: 800,
                  color: "#fff",
                  lineHeight: "72px",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                Trade with Market Sentiment
              </Text>
              <Text
                sx={{
                  mt: 2,
                  fontSize: 20,
                  fontWeight: 400,
                  color: "rgba(255,255,255,0.72)",
                  lineHeight: "30px",
                  fontFamily: "'DM Sans', sans-serif",
                }}
              >
                {HERO_SUBHEAD}
              </Text>
              <Button
                onClick={handleStartInvesting}
                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 24 }} />}
                sx={{
                  alignSelf: "flex-start",
                  mt: 4,
                  width: 215,
                  px: 4,
                  py: "18px",
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 16,
                  color: "#fff",
                  background: CTA_GRADIENT,
                  border: "1px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 10px 12px rgba(85,16,155,0.2)",
                  "&:hover": { background: CTA_GRADIENT, opacity: 0.95 },
                  "& .MuiButton-endIcon": { ml: 1.5, mr: 0 },
                }}
              >
                Start Investing
              </Button>

              <Stack
                direction="row"
                alignItems="center"
                spacing={2}
                sx={{
                  mt: 3.5,
                  width: "100%",
                  maxWidth: 651,
                  height: 60,
                  px: "20px",
                  py: "16px",
                  boxSizing: "border-box",
                  borderRadius: "16px",
                  bgcolor: "rgba(255,255,255,0.04)",
                  border: "1px solid rgba(255,255,255,0.12)",
                  boxShadow: "0 12px 24px rgba(0,0,0,0.2)",
                  backdropFilter: "blur(8px)",
                }}
              >
                {HERO_BENEFITS.map((item, index) => (
                  <React.Fragment key={item.label}>
                    {index > 0 ? (
                      <Box
                        sx={{
                          width: "1px",
                          height: 28,
                          bgcolor: "rgba(255,255,255,0.2)",
                          flexShrink: 0,
                        }}
                      />
                    ) : null}
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ flex: 1, minWidth: 0 }}
                    >
                      <Box
                        sx={{
                          width: 28,
                          height: 28,
                          flexShrink: 0,
                          borderRadius: "14px",
                          bgcolor: "#EDF2FF",
                          display: "grid",
                          placeItems: "center",
                        }}
                      >
                        <Box
                          component="img"
                          src={item.icon}
                          alt=""
                          sx={{ width: 16, height: 16, display: "block" }}
                        />
                      </Box>
                      <Text
                        sx={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: "#DCE4FF",
                          lineHeight: "14px",
                          fontFamily: "'DM Sans', sans-serif",
                        }}
                      >
                        {item.label}
                      </Text>
                    </Stack>
                  </React.Fragment>
                ))}
              </Stack>
            </Stack>

            <Box
              sx={{
                position: "relative",
                width: 357,
                height: 421,
                flexShrink: 0,
                mt: 2,
                overflow: "hidden",
                filter: "drop-shadow(0 18px 40px rgba(0,0,0,0.35))",
              }}
            >
              <Box
                component="img"
                src={ICONS.horse}
                alt="Jarvis Sentiment"
                sx={{
                  position: "absolute",
                  left: "-15.77%",
                  top: "-5.88%",
                  width: "131.54%",
                  height: "111.76%",
                  maxWidth: "none",
                  display: "block",
                  objectFit: "cover",
                }}
              />
            </Box>
          </Stack>

         
          <Stack spacing={1.5} sx={{ display: { xs: "flex", md: "none" } }}>
            <Text
              sx={{
                fontSize: 30,
                fontWeight: 800,
                color: "#fff",
                lineHeight: "38px",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              Trade with Market Sentiment
            </Text>
            <Text
              sx={{
                fontSize: 14,
                color: "rgba(255,255,255,0.72)",
                lineHeight: "21px",
                fontFamily: "'DM Sans', sans-serif",
              }}
            >
              {HERO_SUBHEAD_MOBILE}
            </Text>

            <Box
              sx={{
                position: "relative",
                mx: -3,
                width: "calc(100% + 48px)",
                height: 216,
                mt: 1,
              }}
            >
              {/* Emblem — Figma 7232:10017 @ left 2 / bottom -14 / 174×205 */}
              <Box
                sx={{
                  position: "absolute",
                  left: 2,
                  bottom: -14,
                  width: 174,
                  height: 205,
                  overflow: "hidden",
                  pointerEvents: "none",
                }}
              >
                <Box
                  component="img"
                  src={ICONS.horse}
                  alt="Jarvis Sentiment"
                  sx={{
                    position: "absolute",
                    left: "-15.77%",
                    top: "-5.88%",
                    width: "131.54%",
                    height: "111.76%",
                    maxWidth: "none",
                    display: "block",
                    objectFit: "cover",
                  }}
                />
              </Box>

              {/* CTA — Figma 7069:8766 @ left 190 / 161×42 */}
              <Button
                onClick={handleStartInvesting}
                endIcon={<ArrowForwardRoundedIcon sx={{ fontSize: 24 }} />}
                sx={{
                  position: "absolute",
                  left: 190,
                  right: 24,
                  top: "50%",
                  transform: "translateY(-50%)",
                  maxWidth: 161,
                  height: 42,
                  px: 2,
                  py: 1.75,
                  borderRadius: "14px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: 14,
                  color: "#fff",
                  whiteSpace: "nowrap",
                  background: CTA_GRADIENT_MOBILE,
                  border: "1.5px solid rgba(255,255,255,0.3)",
                  boxShadow: "0 10px 12px rgba(51,65,108,0.2)",
                  "&:hover": { background: CTA_GRADIENT_MOBILE, opacity: 0.95 },
                  "& .MuiButton-endIcon": { ml: "10px", mr: 0 },
                }}
              >
                Start Investing
              </Button>
            </Box>
          </Stack>
        </Box>
      </Box>

      {/* About Sentiment — desktop */}
      <Box sx={{ ...pagePad, py: 7, display: { xs: "none", md: "block" } }}>
        <Text
          sx={{
            textAlign: "center",
            fontSize: 48,
            fontWeight: 700,
            color: "#111827",
            letterSpacing: "-0.02em",
            mb: 5,
          }}
        >
          About Sentiment
        </Text>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "2fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          <Box
            sx={{
              p: 4,
              borderRadius: "20px",
              bgcolor: YEAR_CARD_BG,
              boxShadow: "0 8px 16px rgba(0,0,0,0.08)",
              minHeight: 200,
            }}
          >
            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Box component="img" src={ICONS.frame} alt="" sx={{ width: 28, height: 28 }} />
              <Text
                sx={{
                  bgcolor: "#fff",
                  px: 1.5,
                  py: 0.5,
                  borderRadius: "12px",
                  fontSize: 11,
                  fontWeight: 500,
                  color: ACCENT,
                }}
              >
                Currently AI under reinforcement stage
              </Text>
            </Stack>
            <Text
              sx={{
                mt: 1.5,
                fontSize: 80,
                fontWeight: 700,
                color: "#fff",
                lineHeight: 1,
              }}
            >
              2016
            </Text>
            <Text
              sx={{
                mt: 1,
                fontSize: 20,
                fontWeight: 500,
                color: "rgba(255,255,255,0.9)",
              }}
            >
              AI Engine live in Indian Market since
            </Text>
          </Box>
          <FeatureCard {...FEATURES[0]} />
        </Box>

        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
            mb: 2,
          }}
        >
          {middleFeatures.map((item) => (
            <FeatureCard key={item.text} {...item} />
          ))}
        </Box>

        <FeatureCard {...fullWidthFeature} />
      </Box>

      {/* About Sentiment — mobile card + read more */}
      <Box sx={{ px: 2, py: 2.5, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2}
          sx={{
            p: 2.5,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "24px" }}>
            About Sentiment
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Stack spacing={2.5}>
            {aboutPreview.map((item, index) => (
              <MobileAboutRow key={`about-preview-${index}`} {...item} />
            ))}
          </Stack>
          <Collapse in={aboutExpanded}>
            <Stack spacing={2.5} sx={{ pt: 2.5 }}>
              {aboutRest.map((item, index) => (
                <MobileAboutRow key={`about-rest-${index}`} {...item} />
              ))}
            </Stack>
          </Collapse>
          <Box
            component="button"
            type="button"
            onClick={() => setAboutExpanded((v) => !v)}
            sx={{
              border: 0,
              bgcolor: "transparent",
              cursor: "pointer",
              p: 0,
              alignSelf: "flex-start",
            }}
          >
            <Text sx={{ fontSize: 14, fontWeight: 500, color: "#2977EC", lineHeight: "24px" }}>
              {aboutExpanded ? "read less" : "read more"}
            </Text>
          </Box>
        </Stack>
      </Box>

      {/* Is This For You — desktop */}
      <Box
        sx={{
          display: { xs: "none", md: "block" },
          py: 8,
          px: 7.5,
          bgcolor: "#fff",
          backgroundImage: `
            linear-gradient(rgba(78,99,164,0.06) 1px, transparent 1px),
            linear-gradient(90deg, rgba(78,99,164,0.06) 1px, transparent 1px)
          `,
          backgroundSize: "200px 160px",
        }}
      >
        <Stack
          direction="row"
          spacing={6}
          alignItems="center"
          sx={{ maxWidth: 1320, mx: "auto" }}
        >
          <Stack spacing={2.5} sx={{ flex: "0 1 420px" }}>
            <Box
              sx={{
                width: 64,
                height: 64,
                borderRadius: "50%",
                border: `1.5px solid ${ACCENT}`,
                display: "grid",
                placeItems: "center",
                bgcolor: "#fff",
                fontSize: 36,
                fontWeight: 800,
                color: ACCENT,
              }}
            >
              ?
            </Box>
            <Text
              sx={{
                fontSize: 48,
                fontWeight: 800,
                color: "#1A1A1A",
                lineHeight: 1.1,
              }}
            >
              Is This For You?
            </Text>
            <Text sx={{ fontSize: 16, color: "#6B7280", lineHeight: 1.55 }}>
              A quick checklist to help you decide if our AI-driven portfolio management is the
              right fit for your financial goals.
            </Text>
          </Stack>

          <Box
            sx={{
              flex: 1,
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 2,
              width: "100%",
            }}
          >
            {FOR_YOU_CARDS.map((card) => (
              <ChecklistCard key={card.label} {...card} />
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Is This For You — mobile dark card */}
      <Box sx={{ px: 2, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "24px",
            overflow: "hidden",
            position: "relative",
            border: "1px solid rgba(255,255,255,0.15)",
            background: "linear-gradient(134deg, #131828 0.7%, #4E5E8F 99%)",
            boxShadow:
              "0 0 18px rgba(34,211,238,0.1), 0 10px 24px -10px rgba(29,78,216,0.2), 0 18px 40px -12px rgba(0,0,0,0.15)",
          }}
        >
          <Box
            sx={{
              position: "absolute",
              top: -110,
              right: -90,
              width: 260,
              height: 260,
              borderRadius: "50%",
              background:
                "radial-gradient(circle, rgba(78,99,164,0.45) 0%, transparent 70%)",
              pointerEvents: "none",
            }}
          />
          <Stack spacing={1.25} sx={{ position: "relative" }}>
            <Text sx={{ fontSize: 18, fontWeight: 700, color: "#E6E6E6", lineHeight: "24px" }}>
              Is This For You?
            </Text>
            <Text sx={{ fontSize: 13, color: "rgba(255,255,255,0.8)", lineHeight: "18px" }}>
              A quick checklist to help you decide if this is the right fit.
            </Text>
          </Stack>
          <Box
            sx={{
              borderBottom: "1px solid rgba(255,255,255,0.2)",
              position: "relative",
            }}
          />
          <Stack spacing={1.5} sx={{ position: "relative" }}>
            {MOBILE_FOR_YOU.map((text) => (
              <MobileForYouRow key={text} text={text} />
            ))}
          </Stack>
        </Stack>
      </Box>

      {/* Quick Facts — desktop */}
      <Box sx={{ ...pagePad, py: 7, display: { xs: "none", md: "block" } }}>
        <Text
          sx={{
            textAlign: "center",
            fontSize: 36,
            fontWeight: 700,
            color: "#111827",
            mb: 4,
          }}
        >
          Quick Facts
        </Text>
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 2,
          }}
        >
          {QUICK_FACTS.map((fact) => (
            <FactCard key={`${fact.label}-${fact.value}`} {...fact} />
          ))}
        </Box>
      </Box>

      {/* Quick Facts — mobile 2-col card */}
      <Box sx={{ px: 2, py: 1.25, display: { xs: "block", md: "none" } }}>
        <Stack
          spacing={2.5}
          sx={{
            p: 3,
            borderRadius: "20px",
            bgcolor: "#fff",
            border: "1px solid #E2E8F0",
            boxShadow: "0 4px 6px rgba(0,0,0,0.04)",
          }}
        >
          <Text sx={{ fontSize: 18, fontWeight: 700, color: "#0F172A", lineHeight: "26px" }}>
            Quick Facts
          </Text>
          <Box sx={{ borderBottom: "1px solid #E2E8F0" }} />
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 1.5,
            }}
          >
            {MOBILE_QUICK_FACTS.map((fact) => (
              <MobileFactTile key={`${fact.label}-${fact.value}`} {...fact} />
            ))}
          </Box>
        </Stack>
      </Box>

      {/* Pricing */}
      <Box
        sx={{
          ...pagePad,
          py: { xs: 2.5, md: 10 },
          bgcolor: { xs: "#fff", md: "#F8FAFC" },
          maxWidth: "100%",
        }}
      >
        <Box sx={{ maxWidth: 1320, mx: "auto", width: "100%" }}>
          <Box sx={{ display: { xs: "none", md: "block" } }}>
            <Text
              sx={{
                textAlign: "center",
                fontSize: 48,
                fontWeight: 800,
                color: "#0F172A",
                mb: 1.5,
              }}
            >
              Pricings
            </Text>
            <Text
              sx={{
                textAlign: "center",
                fontSize: 20,
                color: "#475569",
                mb: 5,
              }}
            >
              Choose the plan that fits your investing style
            </Text>
          </Box>
          <SentimentPricingSection plans={plans} onUnlock={handleStartInvesting} />
        </Box>
      </Box>

      <AllInOneFooter />
    </Box>
  );
};

export default SentimentProductInfo;
